import React, { useState, useEffect } from "react";
import MaterialTable from "material-table";
import { Typography, Grid, Card, Box } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "common";
import { formatMoney } from "common/src/other/currencyFormat";
import { ThemeProvider } from "@mui/material/styles";
import GoBackButton from "components/GoBackButton";
import { C2, C2_FONT, c2TableTheme, c2TableOptions, c2Localization } from "../components/Table/C2UserTable";
import localization from "../components/Table/Localization";

const WALLET_TYPE_KEYS = { Credit: "credited", Debit: "debited", Withdraw: "withdrawn" };

const UserWalletDetails = (props) => {
  const { data } = props;
  const { t, i18n } = useTranslation();
  const { fetchUserWalletHistory } = api;
  const isRTL = i18n.dir();
  const settingsdata = useSelector((state) => state.settingsdata);
  const [selectedRow, setSelectedRow] = useState(null);
  const [settings, setSettings] = useState({});
  const navigate = useNavigate();
  const walletHistory = useSelector((state) => state.auth.walletHistory);
  const dispatch = useDispatch();
  const { state } = useLocation();

  useEffect(() => {
    if (settingsdata.settings) {
      setSettings(settingsdata.settings);
    }
  }, [settingsdata.settings]);

  useEffect(() => {
    dispatch(fetchUserWalletHistory(data?.id));
  }, [data, dispatch, fetchUserWalletHistory]);

  const columns = [
    {
      title: t("requestDate"),
      field: "date",
      render: (rowData) => (rowData.date ? moment(rowData.date).format("lll") : null),
    },
    {
      title: t("amount"),
      field: "amount",
      editable: "never",
      render: (rowData) =>
        formatMoney(rowData.amount || 0, settings, { isRTL: settings.swipe_symbol }),
    },
    {
      title: t("transaction_id"),
      field: "transaction_id",
      render: (rowData) =>
        rowData.transaction_id ? rowData.transaction_id : rowData.txRef,
    },
    {
      title: t("type"),
      field: "type",
      render: (rowData) => (
        <Box
          sx={{
            bgcolor:
              rowData.type === "Debit"
                ? "#DF2E38"
                : rowData.type === "Credit"
                ? "#12B76A"
                : "#E6A800",
            color: C2.white,
            px: 1.5,
            py: 0.75,
            borderRadius: "999px",
            fontWeight: 700,
            fontFamily: C2_FONT,
            fontSize: 12,
            width: "fit-content",
            mx: "auto",
          }}
        >
          {t(WALLET_TYPE_KEYS[rowData.type] || rowData.type)}
        </Box>
      ),
    },
  ];

  return (
    <Card
      sx={{
        borderRadius: "20px",
        mb: 2.5,
        overflow: "hidden",
        boxShadow: "0 8px 28px rgba(26,15,59,0.08)",
        border: `1px solid ${C2.border}`,
        bgcolor: C2.white,
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${C2.deep} 0%, ${C2.violet} 55%, ${C2.bright} 100%)`,
          px: { xs: 2, md: 3 },
          pt: 2,
          pb: 3,
        }}
      >
        <GoBackButton
          isRTL={isRTL}
          light
          onClick={() => {
            navigate(`/users/${props.id}`, { state: { pageNo: state?.pageNo } });
          }}
        />
        <Typography
          sx={{
            fontFamily: C2_FONT,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: "rgba(251,250,254,0.75)",
            mt: 1,
          }}
        >
          {t("wallet_ballance") || "Saldo billetera"}
        </Typography>
        <Typography
          sx={{
            fontFamily: C2_FONT,
            fontWeight: 800,
            fontSize: { xs: 28, md: 34 },
            color: C2.white,
            mt: 0.5,
          }}
        >
          {formatMoney(data?.walletBalance || 0, settings, {
            isRTL: !!settings.swipe_symbol,
          })}
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: C2.bg }}>
        <ThemeProvider theme={c2TableTheme}>
          <MaterialTable
            title={t("transaction_history_title")}
            columns={columns}
            style={{
              direction: isRTL === "rtl" ? "rtl" : "ltr",
              borderRadius: "14px",
              boxShadow: "none",
              border: `1px solid ${C2.border}`,
              overflow: "hidden",
            }}
            data={walletHistory ? walletHistory : []}
            onRowClick={(evt, selectedRow) =>
              setSelectedRow(selectedRow.tableData.id)
            }
            options={{
              exportButton: true,
              ...c2TableOptions(selectedRow),
              rowStyle: (rowData) => ({
                backgroundColor:
                  selectedRow === rowData.tableData.id ? C2.lavender : C2.white,
                fontFamily: C2_FONT,
              }),
            }}
            localization={c2Localization(localization(t))}
          />
        </ThemeProvider>
      </Box>
    </Card>
  );
};

export default UserWalletDetails;
